import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Building2, Plus, Users, ArrowRight } from 'lucide-react';
import { useAuth } from '../AuthContext';

const OrganizationSetup = () => {
  const [mode, setMode] = useState('choice'); // 'choice', 'create', 'join'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    joinCode: ''
  });

  const { createOrganization, user } = useAuth();

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await createOrganization(formData.name, formData.description);
      if (!result.success) {
        setError(result.error);
      }
      // Success handled by AuthContext - page will reload with new organization
    } catch (err) {
      setError('Failed to create organization. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <Building2 className="w-8 h-8 text-purple-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            Welcome to What's Happening Events!
          </CardTitle>
          <CardDescription>
            Hi {user?.first_name}! To get started, you need to set up your workspace.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {mode === 'choice' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                Choose how you'd like to get started:
              </p>
              
              <div className="grid grid-cols-1 gap-4">
                <Button
                  variant="outline"
                  className="h-auto p-6 flex flex-col items-center space-y-3 hover:bg-purple-50 hover:border-purple-300"
                  onClick={() => setMode('create')}
                >
                  <Plus className="w-8 h-8 text-purple-600" />
                  <div className="text-center">
                    <h3 className="font-semibold text-lg">Create New Organization</h3>
                    <p className="text-sm text-gray-500">Start fresh with your own workspace</p>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  className="h-auto p-6 flex flex-col items-center space-y-3 hover:bg-blue-50 hover:border-blue-300"
                  onClick={() => setMode('join')}
                >
                  <Users className="w-8 h-8 text-blue-600" />
                  <div className="text-center">
                    <h3 className="font-semibold text-lg">Join Existing Organization</h3>
                    <p className="text-sm text-gray-500">Get invited by an admin</p>
                  </div>
                </Button>
              </div>
            </div>
          )}

          {mode === 'create' && (
            <form onSubmit={handleCreateOrg} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Organization Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your organization name"
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Input
                  id="description"
                  name="description"
                  type="text"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Brief description of your organization"
                  disabled={loading}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMode('choice')}
                  disabled={loading}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !formData.name.trim()}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {loading ? (
                    'Creating...'
                  ) : (
                    <>
                      Create Organization <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}

          {mode === 'join' && (
            <div className="space-y-4">
              <div className="text-center p-6 bg-blue-50 rounded-lg border border-blue-200">
                <Users className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                <h3 className="font-semibold text-lg text-blue-900 mb-2">
                  Ready to Join a Team?
                </h3>
                <p className="text-sm text-blue-700 mb-4">
                  Ask your organization admin to invite you through the user management system.
                  They can add you by going to Settings → User Management.
                </p>
                <p className="text-xs text-blue-600">
                  Once invited, you'll receive an email with instructions to join.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMode('choice')}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={() => setMode('create')}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  Create My Own Instead
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganizationSetup;